import { useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { usePasswordlessSignIn, useSignIn } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const cardSx = {
    width: 648,
    maxWidth: '94vw',
    px: { xs: 2.5, md: 6 },
    py: { xs: 3, md: 4.5 },
    borderRadius: '28px',
    border: '1px solid rgba(214, 55, 49, 0.08)',
    bgcolor: 'rgba(255, 255, 255, 0.96)',
    boxShadow: '0px 24px 70px rgba(61, 61, 61, 0.10)',
    backdropFilter: 'blur(6px)'
};

const fieldSx = {
    '& .MuiInputLabel-root': {
        color: '#2E3442',
        fontWeight: 600
    },
    '& .MuiOutlinedInput-root': {
        bgcolor: '#FFFFFF',
        borderRadius: '16px',
        minHeight: 58,
        '& fieldset': {
            borderColor: '#D8DFEB'
        },
        '&:hover fieldset': {
            borderColor: '#C9D3E2'
        },
        '&.Mui-focused fieldset': {
            borderColor: '#D63731',
            borderWidth: '1px'
        }
    },
    '& .MuiOutlinedInput-input': {
        color: '#101828',
        fontSize: '1rem'
    },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
        WebkitBoxShadow: '0 0 0 100px #FFFFFF inset',
        WebkitTextFillColor: '#101828',
        caretColor: '#101828',
        borderRadius: '12px',
        transition: 'background-color 9999s ease-out 0s'
    }
};

const primaryButtonSx = {
    minHeight: 54,
    width: '100%',
    textTransform: 'none',
    fontWeight: 800,
    fontSize: '1.12rem',
    borderRadius: '14px',
    bgcolor: '#E91C24',
    boxShadow: '0px 12px 24px rgba(233, 28, 36, 0.22)',
    '&:hover': {
        bgcolor: '#D6171F',
        boxShadow: '0px 12px 24px rgba(233, 28, 36, 0.24)'
    },
    '&.Mui-disabled': {
        bgcolor: 'rgba(233, 28, 36, 0.45)',
        color: '#FFFFFF'
    }
};

const iconAdornmentSx = {
    color: '#5F6C80',
    fontSize: 22
};

const otpDigitFieldSx = {
    width: { xs: 42, sm: 52 },
    '& .MuiOutlinedInput-root': {
        height: { xs: 56, sm: 64 },
        borderRadius: '16px',
        bgcolor: '#FFFFFF',
        '& fieldset': {
            borderColor: '#D8DFEB'
        },
        '&:hover fieldset': {
            borderColor: '#C9D3E2'
        },
        '&.Mui-focused fieldset': {
            borderColor: '#D63731',
            borderWidth: '1px'
        }
    },
    '& .MuiOutlinedInput-input': {
        p: 0,
        textAlign: 'center',
        fontSize: { xs: '1.15rem', sm: '1.35rem' },
        fontWeight: 700,
        color: '#101828'
    }
};

const loginModeButtonSx = (selected) => ({
    flex: 1,
    minHeight: 48,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '1rem',
    borderRadius: '14px',
    border: selected ? '1px solid #E91C24' : '1px solid #D8DFEB',
    color: selected ? '#E91C24' : '#5F6C80',
    bgcolor: selected ? 'rgba(233, 28, 36, 0.06)' : '#FFFFFF',
    '&:hover': {
        borderColor: selected ? '#E91C24' : '#C9D3E2',
        bgcolor: selected ? 'rgba(233, 28, 36, 0.08)' : '#FAFBFC'
    }
});

const Login = () => {
    const navigate = useNavigate();
    const formikSignIn = useSignIn();
    const formikPasswordless = usePasswordlessSignIn();
    const [showPassword, setShowPassword] = useState(false);
    const [loginMode, setLoginMode] = useState('password');
    const otpInputRefs = useRef([]);

    const isPasswordMode = loginMode === 'password';
    const passwordlessOtpRequested = formikPasswordless.values.otpRequested;

    const handleModeChange = (mode) => {
        setLoginMode(mode);
        formikSignIn.resetForm();
        formikPasswordless.resetFlow();
        setShowPassword(false);
    };

    const activeFormik = isPasswordMode ? formikSignIn : formikPasswordless;
    const otpDigits = Array.from({ length: 8 }, (_, index) =>
        formikPasswordless.values.code?.[index] || ''
    );

    const updateOtpCode = (nextDigits) => {
        formikPasswordless.setFieldValue('code', nextDigits.join(''), false);
    };

    const handleOtpDigitChange = (index, value) => {
        const cleanValue = value.replace(/\D/g, '');
        const nextDigits = [...otpDigits];

        if (!cleanValue) {
            nextDigits[index] = '';
            updateOtpCode(nextDigits);
            return;
        }

        if (cleanValue.length > 1) {
            cleanValue.slice(0, 8 - index).split('').forEach((char, offset) => {
                nextDigits[index + offset] = char;
            });
            updateOtpCode(nextDigits);
            const focusIndex = Math.min(index + cleanValue.length, 7);
            otpInputRefs.current[focusIndex]?.focus();
            return;
        }

        nextDigits[index] = cleanValue;
        updateOtpCode(nextDigits);

        if (index < 7) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpDigitKeyDown = (index, event) => {
        if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            otpInputRefs.current[index - 1]?.focus();
        }

        if (event.key === 'ArrowRight' && index < 7) {
            event.preventDefault();
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpPaste = (event) => {
        event.preventDefault();
        const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 8);
        if (!pasted) return;

        const nextDigits = Array.from({ length: 8 }, (_, index) => pasted[index] || '');
        updateOtpCode(nextDigits);
        otpInputRefs.current[Math.min(pasted.length - 1, 7)]?.focus();
    };

    return (
        <Stack alignItems="center" spacing={2.5} sx={{ width: '100%' }}>
            <Paper sx={cardSx}>
                <Stack spacing={3}>
                    <Stack spacing={1.2} alignItems="center" textAlign="center">
                        <Typography
                            variant="h3"
                            sx={{
                                color: '#D63731',
                                fontWeight: 900,
                                fontSize: { xs: '2rem', md: '3.15rem' },
                                lineHeight: 1,
                                letterSpacing: '0.01em'
                            }}
                        >
                            INFO.CUIDADOR 2.0
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: '#1F2937',
                                fontSize: { xs: '2rem', md: '2.35rem' }
                            }}
                        >
                            Bem-vindo
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#667085',
                                fontSize: { xs: '1rem', md: '1.15rem' }
                            }}
                        >
                            Aceda à plataforma de gestão para cuidadores
                        </Typography>
                    </Stack>

                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1.25}>
                            <Button
                                type="button"
                                onClick={() => handleModeChange('password')}
                                sx={loginModeButtonSx(isPasswordMode)}
                            >
                                Palavra-passe
                            </Button>
                            <Button
                                type="button"
                                onClick={() => handleModeChange('otp')}
                                sx={loginModeButtonSx(!isPasswordMode)}
                            >
                                Código por email
                            </Button>
                        </Stack>

                        {!isPasswordMode ? (
                            <Typography
                                variant="body2"
                                textAlign="center"
                                sx={{ color: '#667085', fontSize: '0.98rem' }}
                            >
                                {passwordlessOtpRequested
                                    ? 'Introduza o codigo de 8 digitos enviado para o seu email.'
                                    : 'Receba um código temporário por email para iniciar sessão.'}
                            </Typography>
                        ) : null}
                    </Stack>

                    <Box
                        component="form"
                        onSubmit={isPasswordMode ? formikSignIn.handleSubmit : formikPasswordless.handleSubmit}
                    >
                        <Stack spacing={2}>
                            {(isPasswordMode || !passwordlessOtpRequested) && (
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    autoComplete="username"
                                    value={activeFormik.values.email}
                                    onChange={activeFormik.handleChange}
                                    onBlur={activeFormik.handleBlur}
                                    error={Boolean(
                                        activeFormik.touched.email && activeFormik.errors.email
                                    )}
                                    helperText={
                                        activeFormik.touched.email && activeFormik.errors.email
                                            ? activeFormik.errors.email
                                            : ' '
                                    }
                                    sx={fieldSx}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <MailOutlineRoundedIcon sx={iconAdornmentSx} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )}

                            {isPasswordMode ? (
                                <TextField
                                    fullWidth
                                    label="Palavra-passe"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    value={formikSignIn.values.password}
                                    onChange={formikSignIn.handleChange}
                                    onBlur={formikSignIn.handleBlur}
                                    error={Boolean(
                                        formikSignIn.touched.password && formikSignIn.errors.password
                                    )}
                                    helperText={
                                        formikSignIn.touched.password && formikSignIn.errors.password
                                            ? formikSignIn.errors.password
                                            : ' '
                                    }
                                    sx={fieldSx}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockOutlinedIcon sx={iconAdornmentSx} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    sx={{ color: '#5F6C80' }}
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOffOutlinedIcon />
                                                    ) : (
                                                        <VisibilityOutlinedIcon />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            ) : (
                                passwordlessOtpRequested ? (
                                    <Stack spacing={1.5}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                color: '#1F2937',
                                                fontWeight: 700,
                                                textAlign: 'center'
                                            }}
                                        >
                                            Codigo de acesso
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#667085',
                                                textAlign: 'center',
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            Foi enviado um codigo para {formikPasswordless.values.email}.
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            justifyContent="center"
                                            onPaste={handleOtpPaste}
                                        >
                                            {otpDigits.map((digit, index) => (
                                                <TextField
                                                    key={index}
                                                    value={digit}
                                                    inputProps={{
                                                        maxLength: 1,
                                                        inputMode: 'numeric',
                                                        pattern: '[0-9]*'
                                                    }}
                                                    onChange={(event) =>
                                                        handleOtpDigitChange(index, event.target.value)
                                                    }
                                                    onKeyDown={(event) =>
                                                        handleOtpDigitKeyDown(index, event)
                                                    }
                                                    onBlur={formikPasswordless.handleBlur}
                                                    name="code"
                                                    sx={otpDigitFieldSx}
                                                    inputRef={(element) => {
                                                        otpInputRefs.current[index] = element;
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                minHeight: 20,
                                                textAlign: 'center',
                                                color:
                                                    formikPasswordless.touched.code &&
                                                    formikPasswordless.errors.code
                                                        ? '#D63731'
                                                        : '#667085',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            {formikPasswordless.touched.code &&
                                            formikPasswordless.errors.code
                                                ? formikPasswordless.errors.code
                                                : 'Cole ou escreva os 8 digitos do codigo.'}
                                        </Typography>
                                    </Stack>
                                ) : null
                            )}

                            {activeFormik.errors.submit ? (
                                <Alert
                                    severity="error"
                                    sx={{
                                        borderRadius: '16px',
                                        alignItems: 'center'
                                    }}
                                >
                                    {activeFormik.errors.submit}
                                </Alert>
                            ) : null}

                            <Button
                                variant="contained"
                                type="submit"
                                disabled={activeFormik.isSubmitting}
                                sx={primaryButtonSx}
                            >
                                {isPasswordMode
                                    ? formikSignIn.isSubmitting
                                        ? 'A entrar...'
                                        : 'Entrar'
                                    : formikPasswordless.isSubmitting
                                      ? passwordlessOtpRequested
                                          ? 'A validar código...'
                                          : 'A enviar código...'
                                      : passwordlessOtpRequested
                                        ? 'Entrar com código'
                                        : 'Enviar código'}
                            </Button>

                            {!isPasswordMode ? (
                                <>
                                    <Divider sx={{ borderColor: 'rgba(102, 112, 133, 0.14)' }} />
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.25}
                                        justifyContent="space-between"
                                    >
                                        {passwordlessOtpRequested ? (
                                            <Button
                                                type="button"
                                                variant="text"
                                                onClick={formikPasswordless.goBackToEmailStep}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    color: '#667085',
                                                    alignSelf: 'flex-start'
                                                }}
                                            >
                                                Alterar email
                                            </Button>
                                        ) : (
                                            <Box />
                                        )}

                                        <Button
                                            type="button"
                                            variant="text"
                                            onClick={formikPasswordless.resendCode}
                                            disabled={
                                                formikPasswordless.isSubmitting ||
                                                !formikPasswordless.values.email
                                            }
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                color: '#D63731',
                                                alignSelf: 'flex-start'
                                            }}
                                        >
                                            {passwordlessOtpRequested ? 'Reenviar código' : 'Enviar novamente'}
                                        </Button>
                                    </Stack>
                                </>
                            ) : null}
                        </Stack>
                    </Box>

                    <Typography
                        variant="body1"
                        textAlign="center"
                        sx={{ color: '#667085', fontSize: { xs: '0.98rem', md: '1.05rem' } }}
                    >
                        Esqueceu-se da palavra-passe?{' '}
                        <Box
                            component="span"
                            sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationThickness: '1.5px',
                                textUnderlineOffset: '2px',
                                color: '#D63731',
                                fontWeight: 700
                            }}
                            onClick={() => navigate('/forgetPassword')}
                        >
                            Recuperar acesso
                        </Box>
                    </Typography>
                </Stack>
            </Paper>
        </Stack>
    );
};

export default Login;
